import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import './ForwardTest.css';
import './ForwardTest2.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const fmtMoney = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const fmtPct = (n, sign = false) => {
  if (n === null || n === undefined) return '—';
  const v = Number(n);
  const s = v >= 0 ? '+' : '';
  return `${sign ? s : ''}${v.toFixed(2)}%`;
};

const TRACKS = [
  { key: 'mtum', symbol: 'MTUM', label: 'PRODUCTION · MTUM', accent: 'accent-mtum', color: '#d8b4fe', chartName: 'MTUM (production mirror)' },
  { key: 'spmo', symbol: 'SPMO', label: 'CHALLENGER · SPMO', accent: 'accent-spmo', color: '#67e8f9', chartName: 'SPMO (challenger)' },
  { key: 'xmmo', symbol: 'XMMO', label: 'CHALLENGER · XMMO', accent: 'accent-xmmo', color: '#fbbf24', chartName: 'XMMO (challenger)' },
];

const ForwardTest2 = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async (force = false) => {
    try {
      const res = await fetch(`${API_URL}/api/ffsystemlive2/status${force ? '?force=true' : ''}`);
      const json = await res.json();
      if (json.status === 'success') {
        setData(json);
        setError(null);
      } else {
        setError(json.message || 'Simulation unavailable');
      }
    } catch (e) {
      setError('Network error contacting backend');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(() => load(false), 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, []);

  if (loading) {
    return (
      <div className="ft-page">
        <div className="ft-container">
          <div className="ft-empty">Running simulation against latest market data…</div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="ft-page">
        <div className="ft-container">
          <div className="ft-error">{error}</div>
        </div>
      </div>
    );
  }

  const leader = data.leader;
  const leaderTrack = TRACKS.find((t) => t.symbol === leader) || TRACKS[0];

  const SimCard = ({ label, sim, accent, isLeader }) => (
    <div
      className={`sim-card ${accent}`}
      data-testid={`sim-card-${sim.momentum_symbol.toLowerCase()}`}
    >
      <div className="sim-card-header">
        <div className="sim-card-label">{label}</div>
        {isLeader && <span className="sim-leader-tag">LEADING</span>}
      </div>
      <div className="sim-card-symbol">{sim.holding_symbol}</div>
      <div className="sim-card-position">
        {Number(sim.shares).toFixed(2)} sh @ {fmtMoney(sim.avg_entry_price)}
      </div>

      <div className="sim-card-metrics">
        <div className="sim-metric">
          <div className="sim-metric-label">Capital Deployed</div>
          <div className="sim-metric-value">{fmtMoney(sim.market_value)}</div>
          <div className="sim-metric-sub">Cost basis {fmtMoney(sim.cost_basis)}</div>
        </div>
        <div className="sim-metric">
          <div className="sim-metric-label">Equity</div>
          <div className="sim-metric-value">{fmtMoney(sim.equity)}</div>
          <div className="sim-metric-sub">+ {fmtMoney(sim.cash)} cash buffer</div>
        </div>
        <div className="sim-metric">
          <div className="sim-metric-label">Unrealized P/L</div>
          <div
            className={`sim-metric-value ${sim.unrealized_pl >= 0 ? 'up' : 'down'}`}
          >
            {sim.unrealized_pl >= 0 ? '+' : ''}
            {fmtMoney(sim.unrealized_pl)}
          </div>
          <div className="sim-metric-sub">
            {fmtPct(sim.unrealized_pl_pct, true)} on position
          </div>
        </div>
        <div className="sim-metric">
          <div className="sim-metric-label">Total Return</div>
          <div
            className={`sim-metric-value ${sim.total_return_pct >= 0 ? 'up' : 'down'}`}
          >
            {fmtPct(sim.total_return_pct, true)}
          </div>
          <div className="sim-metric-sub">{sim.trade_count} rotations</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="ft-page" data-testid="ffsystemlive2-page">
      <div className="ft-container">
        <div className="ft-hero">
          <div className="ft-kicker">FLIP FLOP · 3-WAY SIMULATION</div>
          <h1 className="ft-title">MTUM vs SPMO vs XMMO — Three-Way Forward Test</h1>
          <p className="ft-sub">
            Pure simulation, no broker. All three systems started with{' '}
            <strong>$100,000</strong> on <strong>{data.sim_start_date}</strong>, deploy{' '}
            <strong>95%</strong> of capital, and execute under identical Serene System logic
            (T+1 adjusted open, 0.15% slippage, 2.0% hysteresis). Only the momentum asset
            differs. Comparison runs against real adjusted market data through{' '}
            <strong>{data.latest_data_date}</strong>.
          </p>
          <div className="ft-nav">
            <a href="/backtest2" className="ft-back-link" data-testid="ft2-back-link">
              ← Back to /backtest2
            </a>
          </div>
        </div>

        {/* Leader banner */}
        <div
          className={`ab-banner banner-${leaderTrack.key}`}
          data-testid="ab-leader-banner"
        >
          <div className="ab-banner-label">CURRENT LEADER</div>
          <div className="ab-banner-symbol">{leader}</div>
          <div className="ab-banner-detail">
            ahead of {data.runner_up} by <strong>{fmtMoney(Math.abs(data.leader_margin_dollars))}</strong>
            {' '}({fmtPct(Math.abs(data.leader_margin_pct), false)}) since {data.sim_start_date}
          </div>
        </div>

        {/* Three sim cards */}
        <div className="sim-grid">
          {TRACKS.map((t) => (
            <SimCard
              key={t.key}
              label={t.label}
              sim={data[t.key]}
              accent={t.accent}
              isLeader={leader === t.symbol}
            />
          ))}
        </div>

        {/* Equity curve: three-way overlay from sim start through today */}
        {(() => {
          const byDate = {};
          TRACKS.forEach((t) => {
            byDate[t.symbol] = Object.fromEntries(
              (data[t.key].daily_equity || []).map((p) => [p.date, p.equity]),
            );
          });
          const allDates = Array.from(
            new Set(TRACKS.flatMap((t) => Object.keys(byDate[t.symbol]))),
          ).sort();
          const chartData = allDates.map((d) => {
            const row = { date: d };
            TRACKS.forEach((t) => { row[t.symbol] = byDate[t.symbol][d] ?? null; });
            row.SpreadSPMO = row.SPMO !== null && row.MTUM !== null
              ? +(row.SPMO - row.MTUM).toFixed(2) : null;
            row.SpreadXMMO = row.XMMO !== null && row.MTUM !== null
              ? +(row.XMMO - row.MTUM).toFixed(2) : null;
            return row;
          });

          return (
            <section className="ft-section" data-testid="ab-equity-section">
              <div className="ft-section-header">
                <h2 className="ft-section-title">
                  Three-Way Equity Curve — MTUM vs SPMO vs XMMO
                </h2>
                <span className="ft-section-count">
                  {chartData.length} trading days
                </span>
              </div>
              <ResponsiveContainer width="100%" height={340}>
                <LineChart
                  data={chartData}
                  margin={{ top: 10, right: 30, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis
                    dataKey="date"
                    stroke="#94a3b8"
                    tick={{ fontSize: 10 }}
                    interval={Math.max(0, Math.floor(chartData.length / 10))}
                  />
                  <YAxis
                    stroke="#94a3b8"
                    domain={['auto', 'auto']}
                    tickFormatter={(v) =>
                      `$${(v / 1000).toFixed(1)}k`
                    }
                  />
                  <Tooltip
                    contentStyle={{
                      background: '#0f172a',
                      border: '1px solid #475569',
                      borderRadius: '8px',
                      color: '#f1f5f9',
                    }}
                    formatter={(value, name) => [
                      value !== null && value !== undefined
                        ? `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : '—',
                      name,
                    ]}
                  />
                  <Legend />
                  <ReferenceLine
                    y={100000}
                    stroke="#475569"
                    strokeDasharray="4 4"
                    label={{ value: 'Start $100k', fill: '#64748b', fontSize: 10, position: 'insideTopLeft' }}
                  />
                  {TRACKS.map((t) => (
                    <Line
                      key={t.symbol}
                      type="monotone"
                      dataKey={t.symbol}
                      name={t.chartName}
                      stroke={t.color}
                      strokeWidth={t.symbol === leader ? 2.5 : 2}
                      dot={false}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>

              <div className="spread-strip" data-testid="ab-spread-strip">
                <span className="spread-label">Challenger spread vs MTUM over time:</span>
                <ResponsiveContainer width="100%" height={90}>
                  <LineChart
                    data={chartData}
                    margin={{ top: 6, right: 30, left: 10, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="date" hide />
                    <YAxis
                      stroke="#64748b"
                      tick={{ fontSize: 9 }}
                      tickFormatter={(v) => (v >= 0 ? `+$${v}` : `-$${Math.abs(v)}`)}
                    />
                    <Tooltip
                      contentStyle={{
                        background: '#0f172a',
                        border: '1px solid #475569',
                        borderRadius: '8px',
                        color: '#f1f5f9',
                        fontSize: '0.8rem',
                      }}
                      formatter={(value, name) => [
                        value !== null && value !== undefined
                          ? `${value >= 0 ? '+' : ''}$${Number(value).toFixed(2)}`
                          : '—',
                        name,
                      ]}
                    />
                    <ReferenceLine y={0} stroke="#475569" strokeDasharray="2 2" />
                    <Line
                      type="monotone"
                      dataKey="SpreadSPMO"
                      name="SPMO − MTUM"
                      stroke="#67e8f9"
                      strokeWidth={1.8}
                      dot={false}
                      connectNulls
                    />
                    <Line
                      type="monotone"
                      dataKey="SpreadXMMO"
                      name="XMMO − MTUM"
                      stroke="#fbbf24"
                      strokeWidth={1.8}
                      dot={false}
                      connectNulls
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </section>
          );
        })()}

        {/* Trade ledgers */}
        <section className="ft-section" data-testid="ab-trades-section">
          <div className="ft-section-header">
            <h2 className="ft-section-title">Simulation Trade Ledger</h2>
            <span className="ft-section-count">
              {TRACKS.reduce((sum, t) => sum + data[t.key].trade_count, 0)} total events
            </span>
          </div>
          <div className="trade-grid">
            {TRACKS.map((t) => (
              <div className="trade-col" key={t.key}>
                <h3 className={`trade-col-title ${t.accent}`}>{t.symbol}</h3>
                <table className="ft-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Action</th>
                      <th>From → To</th>
                      <th>Fill</th>
                      <th>Portfolio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data[t.key].trades.map((tr, idx) => (
                      <tr key={idx}>
                        <td className="ft-mono">{tr.date}</td>
                        <td>
                          <span className={`ft-tag ${tr.action === 'ROTATE' ? 'ft-tag-auto' : 'ft-tag-on'}`}>
                            {tr.action}
                          </span>
                        </td>
                        <td className="ft-mono">
                          {tr.from ? `${tr.from} → ${tr.to}` : tr.to}
                        </td>
                        <td className="ft-mono">{fmtMoney(tr.fill)}</td>
                        <td className="ft-mono">{fmtMoney(tr.portfolio_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </section>

        <div className="sim-disclaimer">
          Simulation is computed nightly against latest adjusted market data. No actual
          broker orders are placed for the challenger tracks. The production MTUM tripwire
          runs live on Alpaca paper — see <a href="/ffsystemlive">/ffsystemlive</a> for the
          broker-fill audit trail.
        </div>
      </div>
    </div>
  );
};

export default ForwardTest2;
